import { fromLonLat } from 'ol/proj';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import GeoJSON from 'ol/format/GeoJSON';
import { boundingExtent } from 'ol/extent';
import { tile as tileStrategy } from 'ol/loadingstrategy';
import { Geometry } from 'ol/geom';
import { Feature } from 'ol';
import { WriteTransactionOptions } from 'ol/format/WFS';

import { LayerManager } from 'models';
import { getTileGrid } from 'utils';
import { SA_BOUNDS, GEOSERVER_URL, MAP_PROJECTION } from 'config';

import { AbstractLayer, AbstractLayerProps, LayerProps } from './AbstractLayer';

export interface Props extends AbstractLayerProps {
    config: LayerManager.LayerConfig;
    url?: string;
}

/**
 * This Layer implements a VectorLayer that connects to a
 * WFS Service. It implements authentication and filtering
 * although to get custom styles, another Layer will need
 * to extend this one.
 */
export class WFSFilteredLayer extends AbstractLayer<
    VectorLayer<VectorSource<Geometry>>
> {
    private url: string | null;

    protected source: VectorSource<Geometry> | null;

    public readonly transactionOptions: WriteTransactionOptions;

    constructor({
        config,
        auth,
        opacity,
        visible,
        zIndex,
        url,
        filter,
    }: Props) {
        super({ config, opacity, visible, zIndex, filter });
        this.auth = auth;
        this.url = url ?? `${GEOSERVER_URL}/risk-planning/ows`;

        this.source = this.buildSource();
        const [featurePrefix, featureType] = config.serviceName?.split(':') ?? [
            '',
            '',
        ];
        this.transactionOptions = {
            featureNS: 'http://cfs.sa.gov.au/risk-planning',
            featurePrefix,
            featureType,
            nativeElements: [],
            version: '1.1.0',
            srsName: MAP_PROJECTION.getCode(),
        };
    }

    initializeLayer(props: LayerProps) {
        return new VectorLayer({
            ...props,
            source: this.source || undefined,
        });
    }

    getFilter() {
        return this.filter;
    }

    getURL() {
        return this.url;
    }

    applyAuthentication() {
        if (this.auth && this.source) {
            this.source.setLoader(
                (extent, resolution, projection, success, failure) => {
                    const proj = projection.getCode();
                    const url = `${
                        this.url
                    }?service=WFS&version=1.0.0&request=GetFeature&typeName=${
                        this.config.serviceName
                    }&outputFormat=application/json&srsname=${proj}&CQL_FILTER=BBOX(geometry, ${extent.join(
                        ',',
                    )},'${proj}') ${this.filter ? ` AND ${this.filter}` : ''}`;
                    const xhr = new XMLHttpRequest();
                    xhr.open('GET', url);
                    const onError = () => {
                        this.source?.removeLoadedExtent(extent);
                        if (failure) failure();
                    };
                    xhr.onerror = onError;
                    xhr.onload = () => {
                        if (xhr.status == 200) {
                            const features = (this.source
                                ?.getFormat()
                                ?.readFeatures(xhr.responseText) ??
                                []) as Feature<Geometry>[];
                            this.source?.addFeatures(features);
                            if (success) success(features);
                        } else {
                            onError();
                        }
                    };

                    if (this.auth)
                        xhr.setRequestHeader(
                            'Authorization',
                            `${this.auth.tokenType} ${this.auth.accessToken}`,
                        );

                    xhr.send();
                },
            );
        }
    }

    private buildSource() {
        if (this.url == null || this.config == null) return null;

        const SRS = MAP_PROJECTION.getCode();

        const coords = SA_BOUNDS;
        const stateExtent = boundingExtent([
            fromLonLat([coords.minLong, coords.minLat], SRS),
            fromLonLat([coords.maxLong, coords.minLat], SRS),
            fromLonLat([coords.maxLong, coords.maxLat], SRS),
            fromLonLat([coords.minLong, coords.minLat], SRS),
        ]);

        const source = new VectorSource({
            url: this.url,
            format: new GeoJSON(),
            strategy: tileStrategy(getTileGrid(SRS, stateExtent)),
        });

        this.source?.dispose();
        this.source = source;
        if (this.layer) this.layer?.setSource(source);

        this.applyAuthentication();
        return source;
    }

    setFilter(filter: string) {
        this.filter = filter;
        this.applyAuthentication();
        this.source?.refresh();
    }
}

export default WFSFilteredLayer;
